// Utility functions for billing cycle calculations with smart date handling

export type BillingCycle = 
  | 'weekly' 
  | 'monthly' 
  | 'every_30_days' 
  | 'bimonthly' 
  | 'every_90_days' 
  | 'every_6_months' 
  | 'yearly';

export interface BillingCycleOption {
  value: BillingCycle;
  label: string;
  description: string;
}

export const BILLING_CYCLES: BillingCycleOption[] = [
  { value: 'weekly', label: 'Weekly', description: 'Every 7 days' },
  { value: 'monthly', label: 'Monthly', description: 'Same date each month (smart date handling)' },
  { value: 'every_30_days', label: 'Every 30 Days', description: 'Exactly every 30 days' },
  { value: 'bimonthly', label: 'Bimonthly', description: 'Every 2 months (smart date handling)' },
  { value: 'every_90_days', label: 'Every 90 Days', description: 'Exactly every 90 days' },
  { value: 'every_6_months', label: 'Every 6 Months', description: 'Every 6 months (smart date handling)' },
  { value: 'yearly', label: 'Yearly', description: 'Same date each year (smart date handling)' },
];

/**
 * Calculates the next renewal date based on billing cycle with smart date handling
 * For monthly, bimonthly, 6-month, and yearly cycles, if the day doesn't exist in the target month,
 * it will use the last day of that month instead.
 */
export function calculateNextRenewalDate(
  startDate: Date, 
  billingCycle: BillingCycle, 
  currentRenewalDate?: Date
): Date {
  const baseDate = currentRenewalDate || startDate;
  const nextDate = new Date(baseDate);

  switch (billingCycle) {
    case 'weekly':
      nextDate.setDate(nextDate.getDate() + 7);
      break;
      
    case 'monthly':
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
      
    case 'every_30_days':
      nextDate.setDate(nextDate.getDate() + 30);
      break;
      
    case 'bimonthly':
      nextDate.setMonth(nextDate.getMonth() + 2);
      break;
      
    case 'every_90_days':
      nextDate.setDate(nextDate.getDate() + 90);
      break;
      
    case 'every_6_months':
      nextDate.setMonth(nextDate.getMonth() + 6);
      break;
      
    case 'yearly':
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
      
    default:
      throw new Error(`Unsupported billing cycle: ${billingCycle}`);
  }

  // Smart date handling for monthly-based cycles
  if (['monthly', 'bimonthly', 'every_6_months', 'yearly'].includes(billingCycle)) {
    return adjustDateForMonthEnd(nextDate, startDate);
  }

  return nextDate;
}

/**
 * Adjusts the date if the target day doesn't exist in the target month
 * For example, if starting on January 31st and moving to February, it will use February 28th/29th
 */
function adjustDateForMonthEnd(targetDate: Date, originalStartDate: Date): Date {
  const originalDay = originalStartDate.getDate();
  const targetMonth = targetDate.getMonth();
  const targetYear = targetDate.getFullYear();
  
  // Get the last day of the target month
  const lastDayOfMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  
  // If the original day doesn't exist in the target month, use the last day
  if (originalDay > lastDayOfMonth) {
    targetDate.setDate(lastDayOfMonth);
  }
  
  return targetDate;
}

/**
 * Gets the display text for a billing cycle
 */
export function getBillingCycleText(billingCycle: BillingCycle): string {
  const option = BILLING_CYCLES.find(cycle => cycle.value === billingCycle);
  return option ? option.label : billingCycle;
}

/**
 * Gets the description for a billing cycle
 */
export function getBillingCycleDescription(billingCycle: BillingCycle): string {
  const option = BILLING_CYCLES.find(cycle => cycle.value === billingCycle);
  return option ? option.description : '';
}

/**
 * Formats a billing cycle value for display
 * Converts 'every_90_days' to 'Every 90 Days'
 */
export function formatBillingCycleForDisplay(billingCycle: string): string {
  // First try to find it in our BILLING_CYCLES array for proper formatting
  const option = BILLING_CYCLES.find(cycle => cycle.value === billingCycle);
  if (option) {
    return option.label;
  }
  
  // Fallback: convert underscores to spaces and capitalize
  return billingCycle
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
